import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface BusinessStage {
  id: string;
  brand_id: string;
  name: string;
  type: string;
  description: string | null;
  sort_order: number;
  color: string;
  icon: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CreateBusinessStageInput {
  brand_id: string;
  name: string;
  type?: string;
  description?: string | null;
  sort_order?: number;
  color?: string;
  icon?: string | null;
  is_active?: boolean;
}

interface UpdateBusinessStageInput {
  id: string;
  name?: string;
  type?: string;
  description?: string | null;
  sort_order?: number;
  color?: string;
  icon?: string | null;
  is_active?: boolean;
}

const DEFAULT_STAGES = [
  { name: 'Awareness', type: 'lifecycle', color: '#3b82f6', sort_order: 0 },
  { name: 'Pre-Launch', type: 'lifecycle', color: '#8b5cf6', sort_order: 1 },
  { name: 'Early-Traction', type: 'lifecycle', color: '#f59e0b', sort_order: 2 },
  { name: 'Growth', type: 'lifecycle', color: '#22c55e', sort_order: 3 },
];

export const useBusinessStages = (brandId: string) => {
  const queryClient = useQueryClient();

  const fetchBusinessStages = async (): Promise<BusinessStage[]> => {
    const { data, error } = await supabase
      .from('os_business_stages')
      .select('*')
      .eq('brand_id', brandId)
      .order('sort_order', { ascending: true });

    if (error) {
      throw error;
    }

    return (data || []) as BusinessStage[];
  };

  const { data: businessStages = [], isLoading, error } = useQuery({
    queryKey: ['business-stages', brandId],
    queryFn: fetchBusinessStages,
    enabled: !!brandId,
  });

  const createBusinessStage = useMutation({
    mutationFn: async (input: CreateBusinessStageInput) => {
      const { data, error } = await supabase
        .from('os_business_stages')
        .insert({
          brand_id: input.brand_id,
          name: input.name,
          type: input.type || 'lifecycle',
          description: input.description || null,
          sort_order: input.sort_order ?? businessStages.length,
          color: input.color || '#6366f1',
          icon: input.icon || null,
          is_active: input.is_active ?? true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-stages', brandId] });
      toast.success('Business stage created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create business stage: ${error.message}`);
    },
  });

  const updateBusinessStage = useMutation({
    mutationFn: async (input: UpdateBusinessStageInput) => {
      const { id, ...rest } = input;
      
      const { data, error } = await supabase
        .from('os_business_stages')
        .update(rest)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-stages', brandId] });
      toast.success('Business stage updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update business stage: ${error.message}`);
    },
  });

  const deleteBusinessStage = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_business_stages')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-stages', brandId] });
      toast.success('Business stage deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete business stage: ${error.message}`);
    },
  });

  const reorderBusinessStages = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const updates = orderedIds.map((id, index) => ({
        id,
        sort_order: index,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('os_business_stages')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-stages', brandId] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to reorder stages: ${error.message}`);
    },
  });

  const seedDefaultStages = useMutation({
    mutationFn: async () => {
      const stagesToInsert = DEFAULT_STAGES.map(stage => ({
        brand_id: brandId,
        ...stage,
      }));

      const { error } = await supabase
        .from('os_business_stages')
        .insert(stagesToInsert);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-stages', brandId] });
      toast.success('Default stages created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to seed default stages: ${error.message}`);
    },
  });

  return {
    businessStages,
    isLoading,
    error,
    createBusinessStage,
    updateBusinessStage,
    deleteBusinessStage,
    reorderBusinessStages,
    seedDefaultStages,
  };
};
